import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Form, Modal,
} from 'react-bootstrap';
import BillingoService from '../../../Services/BillingoService';
import { apiErrorText } from '../../../Utils/billingoDocuments';

// The reasons the desk actually hides a booking for, one click each; the text
// stays editable.
export const HIDE_REASONS = [
  'Invoiced outside Billingo',
  'Billed monthly to the partner',
  'Not billable (free or internal trip)',
  'Duplicate booking',
];

const MAX_REASON = 200;

function HideReasonModal({
  count, show, busy, error, onHide, onConfirm,
}) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (show) setReason(''); }, [show]);
  const trimmed = reason.trim();

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title className="fs-5">
          {`Hide ${count} booking${count > 1 ? 's' : ''} for everyone`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
        <p className="small text-muted">
          Hidden bookings leave the invoicing queues for the whole team. Say why, so
          whoever finds them under Hidden later knows.
        </p>
        <div className="d-flex flex-wrap gap-2 mb-2">
          {HIDE_REASONS.map((r) => (
            <Button
              key={r}
              size="sm"
              variant={trimmed === r ? 'primary' : 'light'}
              className="filter-chip"
              aria-pressed={trimmed === r}
              onClick={() => setReason(r)}
            >
              {r}
            </Button>
          ))}
        </div>
        <Form.Group controlId="hide-reason">
          <Form.Label className="small mb-1">Reason</Form.Label>
          <Form.Control
            value={reason}
            maxLength={MAX_REASON}
            placeholder="e.g. invoiced on the partner's monthly statement"
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button onClick={() => onConfirm(trimmed)} disabled={busy || !trimmed}>
          {busy ? 'Hiding…' : 'Hide'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Hiding and unhiding Invoicing rows (groups of bookings) for everyone — one
 * implementation for the Missing documents backlog and the final-Számla
 * queue. Hiding asks for a reason first (the API requires one); unhiding
 * needs none. `onChanged` reloads the list after either.
 *
 * Returns `hide(groups)`, `unhide(groups)`, `busy`, `error` (an unhide's
 * failure — a hide's is shown in its dialog) and the dialog to render.
 */
export default function useHideBookings({ onChanged = () => {} } = {}) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async (groups, hidden, reason) => {
    setBusy(true);
    setError('');
    try {
      await BillingoService.setMissingDocumentsHidden(
        groups.flatMap((g) => g.booking_ids),
        hidden,
        reason,
      );
      setPending(null);
      await onChanged();
    } catch (err) {
      setError(apiErrorText(err, 'Could not update the bookings.'));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setPending(null);
    setError('');
  };

  const modal = (
    <HideReasonModal
      count={(pending || []).flatMap((g) => g.booking_ids).length}
      show={!!pending}
      busy={busy}
      error={pending ? error : ''}
      onHide={close}
      onConfirm={(reason) => save(pending, true, reason)}
    />
  );

  return {
    hide: (groups) => { setError(''); setPending(groups); },
    unhide: (groups) => save(groups, false),
    busy,
    error: pending ? '' : error,
    modal,
  };
}
